import { useState } from 'react';
import { canScheduleVariant, isInPast, normalizeToUtc, toIsoUtc } from '@asp/domain';
import type { VariantState } from '@asp/domain';
import { useDictionary } from '../i18n/context.js';
import { Stamp } from './Stamp.js';

// ---------------------------------------------------------------------------
// The schedule panel (UI structure spec section 12). Takes browser-local
// input, shows the normalized UTC instant before saving, and blocks a past
// instant. Reuses @asp/domain's own `normalizeToUtc`/`isInPast` -- the exact
// functions `scheduleVariant` (packages/application/src/schedule/schedule-variant.ts)
// re-checks server-side -- so the preview and the eventual server decision
// can never disagree about what a given local input means in UTC.
//
// No timezone is ever sent to or stored by the server: `publishAt` carries
// the local wall-clock reading plus its numeric UTC offset (e.g.
// "2026-08-14T15:30:00+09:00"), and the server does its own, independent
// UTC conversion from that -- the browser's role is only to preview it.
// ---------------------------------------------------------------------------

const offsetString = (date: Date): string => {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
};

const toRfc3339 = (localInputValue: string): string => {
  // <input type="datetime-local"> yields "YYYY-MM-DDTHH:mm" with no seconds
  // and no zone. `new Date(value)` (no zone) is parsed by every evergreen
  // engine as *local* time, which is exactly the reading whose offset we
  // need for the explicit-offset instant the server expects.
  const asLocal = new Date(localInputValue);
  return `${localInputValue}:00${offsetString(asLocal)}`;
};

export type SchedulePanelProps = {
  state: VariantState;
  approvedRevisionId: string | null;
  currentSchedulePublishAtUtc: string | null;
  onSave: (publishAtRfc3339: string) => Promise<void>;
  saving?: boolean;
};

export const SchedulePanel = ({
  state, approvedRevisionId, currentSchedulePublishAtUtc, onSave, saving = false,
}: SchedulePanelProps): React.JSX.Element => {
  const dict = useDictionary();
  const [localInput, setLocalInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Whole-branch review C2: this used to gate only on
  // `approvedRevisionId !== null`, which survives publication -- a
  // `published` variant still has an approved revision, so the schedule
  // panel stayed enabled after the post already went out. `canScheduleVariant`
  // (@asp/domain) is the exact precondition `scheduleVariant`
  // (packages/application/src/schedule/schedule-variant.ts) enforces --
  // the same predicate `apps/worker/src/mcp/tools.ts`'s `computeNextActions`
  // uses for the `schedule_variant` MCP tool, so the UI and MCP surfaces
  // can no longer disagree about when scheduling is allowed.
  const canSchedule = canScheduleVariant(state, approvedRevisionId);

  let previewUtc: Date | null = null;
  let rfc3339: string | null = null;
  if (localInput) {
    rfc3339 = toRfc3339(localInput);
    try {
      previewUtc = normalizeToUtc(rfc3339);
    } catch {
      previewUtc = null;
    }
  }

  const handleSave = async (): Promise<void> => {
    if (!localInput || !rfc3339) {
      setError(dict.ui.schedulePanel.requiredError);
      return;
    }
    const utc = normalizeToUtc(rfc3339);
    if (isInPast(utc, new Date())) {
      setError(dict.ui.schedulePanel.pastError);
      return;
    }
    setError(null);
    await onSave(rfc3339);
  };

  return (
    <div className="panel">
      <div className="panel__head">
        <h2 className="t-section">{dict.ui.schedulePanel.title}</h2>
        {(() => {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          return <span className="mono-meta">{tz}</span>;
        })()}
      </div>
      <div className="panel__body stack">
        {currentSchedulePublishAtUtc && (
          <div className="stack-1">
            <span className="t-meta">{dict.ui.schedulePanel.currentlyScheduled}</span>
            <Stamp instant={currentSchedulePublishAtUtc} />
          </div>
        )}

        {!canSchedule && (
          <div className="blocked">
            <span className="blocked__why">{dict.ui.schedulePanel.needsApproval}</span>
          </div>
        )}

        <div className="field" style={{ maxWidth: 320 }}>
          <label className="field__label" htmlFor="schedule-local-input">{dict.ui.schedulePanel.localLabel}</label>
          <input
            id="schedule-local-input"
            className={error ? 'input input--error' : 'input'}
            type="datetime-local"
            disabled={!canSchedule || saving}
            value={localInput}
            onChange={(e) => { setLocalInput(e.target.value); setError(null); }}
          />
          {error && <div className="field__error">{error}</div>}
        </div>

        {previewUtc && (
          <div className="stack-1">
            <span className="t-label">{dict.ui.schedulePanel.utcLabel}</span>
            <Stamp instant={previewUtc.toISOString()} />
            <span className="mono-meta">{toIsoUtc(previewUtc)}</span>
          </div>
        )}

        <div>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!canSchedule || !localInput || saving}
            onClick={() => { void handleSave(); }}
          >
            {dict.ui.schedulePanel.save}
          </button>
        </div>
      </div>
    </div>
  );
};
